<!--
  FlatMenuRow — single row of the flat (no-cauldron) menu.

  Looks like a normal list-item button — full-width row, rounded
  background on hover, emerald tint when active. ONLY the icon's
  x-position mirrors `RailButton`: the leading 64px of the row matches
  the rail's `w-16` icon column, with the icon centred inside a
  `w-10 h-10` cell at `mx-3`. So collapsing the sidebar to the rail
  leaves the icons at the exact same screen position; the label simply
  disappears with the rest of the row.
-->
<template>
  <component
    :is="disabled ? 'span' : 'router-link'"
    :to="disabled ? undefined : to"
    :role="disabled ? 'link' : undefined"
    :aria-disabled="disabled ? 'true' : undefined"
    :tabindex="disabled ? -1 : undefined"
    class="flat-menu-row group flex items-center h-10 mb-1 rounded-lg transition-colors focus:outline-none focus-visible:shadow-theme-focus"
    :class="[
      disabled ? 'opacity-50 cursor-not-allowed' : '',
      isActive
        ? 'bg-theme-palette-emerald/10 dark:bg-theme-palette-emerald/20'
        : 'hover:bg-theme-bg-subtle dark:hover:bg-theme-bg/50',
    ]"
    :title="label"
    @click="!disabled && emit('click')"
  >
    <!-- Icon cell — same w-10 / mx-3 geometry as `RailButton` so its
         icon sits at x = 22px from the row's left edge (matching the
         collapsed rail). No separate background on the cell; the row
         itself carries the hover/active tint. -->
    <span class="flex items-center justify-center w-10 h-10 mx-3 shrink-0">
      <icon
        :path="icon"
        size="20"
        :class="[
          isActive
            ? 'text-theme-palette-emerald dark:text-theme-palette-emerald'
            : 'text-theme-text-secondary group-hover:text-theme-text-secondary dark:group-hover:text-theme-text-muted',
        ]"
      />
    </span>
    <span
      class="text-sm font-medium truncate min-w-0 flex-1 pr-3 transition-colors"
      :class="[
        isActive
          ? 'text-theme-palette-emerald dark:text-theme-palette-emerald'
          : 'text-theme-text-secondary group-hover:text-theme-text dark:group-hover:text-theme-text-muted',
      ]"
    >
      {{ label }}
    </span>
  </component>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';

  const props = defineProps({
    to: { type: [String, Object], required: true },
    label: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    icon: { type: String, required: true },
    /** How the active state matches `route.path` against `to`. */
    activeMatch: {
      type: String,
      default: 'exact',
      validator: (v) => ['exact', 'prefix'].includes(v),
    },
  });
  const emit = defineEmits(['click']);

  const route = useRoute() || {};

  const toPath = computed(() => (typeof props.to === 'string' ? props.to : props.to?.path || ''));

  const isActive = computed(() => {
    const current = route.path || '';
    const target = toPath.value;
    if (!target) return false;
    if (props.activeMatch === 'prefix') {
      return current === target || current.startsWith(`${target}/`);
    }
    return current === target;
  });
</script>
